// Partner Studio — panggilan ke callable `partnerStudio` (functions/partnerStudio.js).
// Semua data Partner duduk di server; page ini TIDAK membaca/menulis Firestore `partners/` terus.
//
// Token sesi PIN disimpan dalam sessionStorage, diikat pada uid (login akaun lain dalam tab
// yang sama tidak mewarisi sesi). Server tetap menyemak token pada setiap aksi — storan ini
// hanya kemudahan supaya Partner tidak perlu taip PIN setiap kali halaman dimuat semula.
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';

const KUNCI_TOKEN = 'sesaat_partner_sesi_v1';

export const MODE_KUNCI = { 1: 'mode1_noAvatarNoGender', 2: 'mode2_noAvatarWithGender', 3: 'mode3_withAvatar' };
export const MODE_LABEL = { 1: 'Mode 1 · no avatar', 2: 'Mode 2 · with gender', 3: 'Mode 3 · with avatar' };
/** Format video module Partner (29 Okt) — KEMBAR FORMAT_SAH di functions/partnerStudio.js. 3–5 scene menyusul (Generate Resipi). */
export const FORMAT_PILIHAN = [{ n: 1, label: '1 scene · 8s' }, { n: 2, label: '2 scenes · 16s' }];
/** Engine module rasmi yang serasi dengan format. ⚠️ KEMBAR ENGINE_IKUT_FORMAT di functions/partnerStudio.js (server menolak yang lain). */
export const ENJIN_IKUT_FORMAT = { 1: ['flow1', 'resipi'], 2: ['flow2', 'resipi'] };
// Medan output setiap set yang module keluarkan (untuk panel "Apa module ni keluarkan"). Ikut format.
export const medanOutput = (format) => (Number(format) === 2
    ? ['Hook', 'Image prompt', 'Script · scene 1', 'Video prompt · scene 1', 'Script · scene 2', 'Video prompt · scene 2', 'Caption']
    : ['Hook', 'Image prompt', 'Script', 'Video prompt', 'Caption']);
// Rangka berlabel yang Partner mula (titik permulaan, boleh diubah). SESAAT tukar label → nama medan JSON di belakang.
export const rangkaPrompt = (format) => {
    const dua = Number(format) === 2;
    return [
        'You are a TikTok content creator. Make 3 content sets for the product, each a different angle.',
        '',
        '[HOOK] — your rule for the hook text (Bahasa Melayu Malaysia, 5-12 words, casual):',
        '',
        '',
        '[IMAGE PROMPT] — the model, background, and how they hold the product (English):',
        '',
        '',
        dua ? '[SCRIPT] — the 8-second spoken voiceover for EACH of the 2 scenes (Bahasa Melayu Malaysia):'
            : '[SCRIPT] — the 8-second spoken voiceover (Bahasa Melayu Malaysia):',
        '',
        '',
        dua ? '[VIDEO PROMPT] — camera movement and action for scene 1 and scene 2 (English):'
            : '[VIDEO PROMPT] — camera movement and action (English):',
        '',
        '',
        '[CAPTION] — the caption plus 3-5 hashtags (Bahasa Melayu Malaysia):',
        '',
    ].join('\n');
};
export const labelFormat = (n) => FORMAT_PILIHAN.find((x) => x.n === Number(n))?.label || '';

export function bacaToken() {
    try {
        const s = JSON.parse(sessionStorage.getItem(KUNCI_TOKEN) || 'null');
        return s && s.uid === auth.currentUser?.uid ? s.token : null;
    } catch { return null; }
}
export function simpanToken(token) {
    try { sessionStorage.setItem(KUNCI_TOKEN, JSON.stringify({ uid: auth.currentUser?.uid, token })); } catch { /* storan disekat — Partner taip PIN setiap muat semula */ }
}
export function buangToken() {
    try { sessionStorage.removeItem(KUNCI_TOKEN); } catch { /* abaikan */ }
}

/** "2h ago" / "yesterday" — masa relatif ringkas untuk senarai Studio. */
export function masaLalu(masaMs) {
    if (!masaMs) return '';
    const saat = Math.max(0, Math.round((Date.now() - masaMs) / 1000));
    if (saat < 60) return 'just now';
    const minit = Math.round(saat / 60);
    if (minit < 60) return `${minit} min ago`;
    const jam = Math.round(minit / 60);
    if (jam < 24) return `${jam}h ago`;
    const hari = Math.round(jam / 24);
    if (hari === 1) return 'yesterday';
    if (hari < 30) return `${hari} days ago`;
    return new Date(masaMs).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Status semakan satu module Partner (Fasa 3A) → { kunci, label, tone, ket }.
 * kunci = menunggu | perlu_ubah | lulus | draf (dipakai tapisan laman Studio).
 * tone = tone <Badge> v3.9 (neutral | brand | ok | warn).
 */
export function statusModul(m) {
    const s = m?.semakan;
    if (s?.status === 'menunggu') {
        return { kunci: 'menunggu', label: 'In review', tone: 'brand', ket: `version ${s.versi} sent ${masaLalu(s.dihantarPada)}${m.versiLulus ? ` · version ${m.versiLulus} stays approved meanwhile` : ''}` };
    }
    if (s?.status === 'perlu_ubah') {
        return { kunci: 'perlu_ubah', label: 'Changes requested', tone: 'warn', ket: `SESAAT: "${s.notaSesaat}"` };
    }
    if (m?.versiLulus) {
        const lebih = (m.versiSemasa || 0) > m.versiLulus;
        return { kunci: 'lulus', label: `Approved v${m.versiLulus}`, tone: 'ok', ket: lebih ? `version ${m.versiSemasa} saved since — send it to update` : 'latest version is approved' };
    }
    return { kunci: 'draf', label: 'Draft', tone: 'neutral', ket: '' };
}

/** Teks status auto-save draf ke server (editor + test lab). */
export function teksStatusDraf(st) {
    if (!st) return '';
    if (st.keadaan === 'menyimpan') return 'Saving…';
    if (st.keadaan === 'disimpan') return `Saved ${masaLalu(st.masa)}`;
    if (st.keadaan === 'ralat') return `Not saved: ${st.ralat}`;
    if (st.keadaan === 'konflik') return 'Not saved — your prompt was changed in another tab or device. Choose which to keep above.';
    return '';
}

const kodRalat = (e) => String(e?.code || '').replace(/^functions\//, '');

/** Sesi tamat di server → page wajib kembali ke skrin PIN. */
export const sesiTamat = (e) => kodRalat(e) === 'unauthenticated' && e?.details?.sesi === 'tamat';
export { kodRalat };

/*
 * Bug 7 (1 Nov): sambungan putus / server tak menjawab → SDK Firebase memberi ralat yang ayatnya SAMA dengan
 * kodnya ("internal", "deadline-exceeded"), dan page dahulu memapar perkataan "internal" sahaja. Server Partner
 * SENTIASA menghantar ayat sendiri (tolak(kod, mesej)), jadi ayat == kod bermakna jawapan server tidak sampai.
 */
const KOD_RANGKAIAN = ['internal', 'deadline-exceeded', 'unavailable', 'unknown'];
export const ralatRangkaian = (e) => !!e?.rangkaian || (KOD_RANGKAIAN.includes(kodRalat(e)) && (!e?.message || e.message === kodRalat(e)));
function ralatRangkaianJelas(e) {
    const r = new Error(kodRalat(e) === 'deadline-exceeded'
        ? 'SESAAT took too long to answer. Check your internet connection, then try again.'
        : "Couldn't reach SESAAT. Check your internet connection, then try again.");
    r.code = e?.code; r.details = e?.details; r.rangkaian = true;
    return r;
}

/**
 * Panggil satu aksi. Aksi Studio dihantar bersama token sesi.
 * Ralat sesi tamat → token dibuang (pemanggil memaparkan skrin PIN).
 * Ralat rangkaian → ayat jelas + `rangkaian: true` (pemanggil boleh segar semula kuota / Recent tests).
 */
export async function panggilPartner(aksi, data = {}, { timeout = 60000 } = {}) {
    const call = httpsCallable(functions, 'partnerStudio', { timeout });
    try {
        const { data: hasil } = await call({ aksi, ...data, token: bacaToken() });
        return hasil;
    } catch (e) {
        if (sesiTamat(e)) buangToken();
        if (ralatRangkaian(e)) throw ralatRangkaianJelas(e);
        throw e;
    }
}
